// Document Design Service - Brand Library photo catalogue via Azure Function (doc_design schema)
import { authService } from '../auth/authService';

class DocumentDesignService {
  constructor() {
    this.functionAppUrl = 'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';
    this.apiUrl = `${this.functionAppUrl}/api/document_design`;
  }

  async getAuthenticatedHeaders(contentType = 'application/json') {
    try {
      const token = await authService.getAccessToken('qgisApi');
      const userInfo = authService.getUserInfo();

      return {
        Authorization: `Bearer ${token}`,
        'Content-Type': contentType,
        'x-user-id': userInfo?.email || userInfo?.username || 'unknown',
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication failed');
    }
  }

  async request(path, { method = 'GET', params, body, rawBody, contentType } = {}) {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== false) {
        query.set(key, String(value));
      }
    });
    const qs = query.toString();
    const url = `${this.apiUrl}/${path}${qs ? `?${qs}` : ''}`;

    const headers = await this.getAuthenticatedHeaders(contentType);
    const response = await fetch(url, {
      method,
      headers,
      body: rawBody ?? (body !== undefined ? JSON.stringify(body) : undefined),
    });

    const text = await response.text();
    let result = {};
    try {
      result = text ? JSON.parse(text) : {};
    } catch {
      result = { message: text };
    }
    if (!response.ok) {
      throw new Error(result.message || result.error || `Request failed: ${response.status}`);
    }
    return result;
  }

  async getStatus() {
    const result = await this.request('status');
    return result.data;
  }

  async getFolders() {
    const result = await this.request('folders');
    return result.data || [];
  }

  /**
   * @param {object} filters - { q, search, category, subcategory, orientation, approved, flagged,
   *                            min_width, min_height, include_pending, limit, offset }
   */
  async listMedia(filters = {}) {
    const result = await this.request('media', { params: filters });
    return {
      items: result.data || [],
      total: result.total || 0,
      searchMode: result.search_mode || null,
    };
  }

  async getMedia(id) {
    const result = await this.request(`media/${id}`);
    return result.data;
  }

  /** Fields: approved_external, photo_credit, date_taken (YYYY-MM-DD), photo_location, people_consent, photo_notes, user_tags */
  async updateMedia(id, fields) {
    const result = await this.request(`media/${id}`, { method: 'PATCH', body: fields });
    return result.data;
  }

  /** { compositions, documents } that reference this asset. */
  async getMediaUsage(id) {
    const result = await this.request(`media/${id}/usage`);
    return result.data;
  }

  /** Deletes the file from SharePoint (recycle bin), the Brand Library and blob storage. */
  async deleteMedia(id) {
    const result = await this.request(`media/${id}`, { method: 'DELETE' });
    return result.data;
  }

  /** Re-run OpenAI vision tagging + embedding for one photo. */
  async enrichMedia(id) {
    const result = await this.request(`media/${id}/enrich`, { method: 'POST' });
    return result.data;
  }

  async uploadMedia(file, folderPath) {
    const result = await this.request('media', {
      method: 'POST',
      params: { folder: folderPath, filename: file.name },
      rawBody: file,
      contentType: file.type || 'application/octet-stream',
    });
    return result.data;
  }

  async runSync() {
    const result = await this.request('sync', { method: 'POST' });
    return result.data;
  }

  // ---- Documents (Word ingestion + review) ----

  /** Signed-in user's documents. Admins may pass { scope: 'all' } to list everyone's. */
  async listDocuments(params = {}) {
    const result = await this.request('documents', { params });
    return { items: result.data || [], viewer: result.viewer || null, scope: result.scope || 'mine' };
  }

  /** Upload a .docx as a new document, or as a new version when documentId is given. */
  async uploadDocument(file, documentId) {
    const result = await this.request('documents', {
      method: 'POST',
      params: { filename: file.name, document_id: documentId },
      rawBody: file,
      contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    return result.data;
  }

  async getDocument(id) {
    const result = await this.request(`documents/${id}`);
    return result.data;
  }

  /** Fields: title, project, report_type, theme_key, is_archived */
  async updateDocument(id, fields) {
    const result = await this.request(`documents/${id}`, { method: 'PATCH', body: fields });
    return result.data;
  }

  /** Permanently delete a document, all versions, review data, and stored blobs. */
  async deleteDocument(id) {
    const result = await this.request(`documents/${id}`, { method: 'DELETE' });
    return result.data;
  }

  /** params: version, type (comma list), q, low_confidence, from_seq, include_data, limit, offset */
  async listBlocks(id, params = {}) {
    const result = await this.request(`documents/${id}/blocks`, { params });
    return { items: result.data || [], total: result.total || 0 };
  }

  async listIssues(id, params = {}) {
    const result = await this.request(`documents/${id}/issues`, { params });
    return result.data || [];
  }

  async listAssets(id, params = {}) {
    const result = await this.request(`documents/${id}/assets`, { params });
    return result.data || [];
  }

  /** overrides: [{ block_key, action, level?, note? }]; action 'clear' removes the override */
  async setOverrides(id, overrides) {
    const result = await this.request(`documents/${id}/overrides`, { method: 'POST', body: { overrides } });
    return result.data;
  }

  // ---- Layout + render ----

  async getLayout(id, params = {}) {
    const result = await this.request(`documents/${id}/layout`, { params });
    return result.data;
  }

  /** body: { action: 'auto' } | { action: 'set', slot_key, media_asset_id|null } | { action: 'clear', slot_key }
   *        | { action: 'options', options: {...} } */
  async updateLayout(id, body) {
    const result = await this.request(`documents/${id}/layout`, { method: 'POST', body });
    return result.data;
  }

  async getPhotoCandidates(id, params = {}) {
    const result = await this.request(`documents/${id}/photos`, { params });
    return result.data || [];
  }

  /** Print HTML for the report; paginated in an iframe by Paged.js. */
  async getRenderHtml(id, params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && v !== null && v !== '' && query.set(k, String(v)));
    const headers = await this.getAuthenticatedHeaders();
    const response = await fetch(`${this.apiUrl}/documents/${id}/render?${query}`, { headers });
    const text = await response.text();
    if (!response.ok) {
      let message = text;
      try {
        const parsed = JSON.parse(text);
        message = parsed.message || parsed.error || text;
      } catch {
        // non-JSON error body
      }
      throw new Error(message || `Render failed: ${response.status}`);
    }
    return text;
  }

  async logRender(id, body) {
    const result = await this.request(`documents/${id}/renders`, { method: 'POST', body });
    return result.data;
  }

  async reparseDocument(id, versionNo) {
    const result = await this.request(`documents/${id}/reparse`, { method: 'POST', params: { version: versionNo } });
    return result.data;
  }

  // ---- Media Creator ----

  /** Signed-in user's compositions. Admins may pass { scope: 'all' } to list everyone's. */
  async listCompositions(params = {}) {
    const result = await this.request('compositions', { params });
    return {
      items: result.data || [],
      presets: result.presets || {},
      viewer: result.viewer || null,
      scope: result.scope || 'mine',
    };
  }

  async getComposition(id) {
    const result = await this.request(`compositions/${id}`);
    return result.data;
  }

  async createComposition(body) {
    const result = await this.request('compositions', { method: 'POST', body });
    return result.data;
  }

  async updateComposition(id, body) {
    const result = await this.request(`compositions/${id}`, { method: 'PATCH', body });
    return result.data;
  }

  async deleteComposition(id) {
    const result = await this.request(`compositions/${id}`, { method: 'DELETE' });
    return result.data;
  }

  async regenerateComposition(id, body) {
    const result = await this.request(`compositions/${id}/regenerate`, { method: 'POST', body });
    return result.data;
  }

  async refineComposition(id, body) {
    const result = await this.request(`compositions/${id}/refine`, { method: 'POST', body });
    return result.data;
  }

  mapListMediaToCandidate(item) {
    const rend = item.renditions || {};
    const thumb = rend.thumb?.url;
    const web = rend.web?.url;
    return {
      id: item.id,
      media_asset_id: item.id,
      file_name: item.file_name,
      folder_path: item.folder_path,
      description: item.description,
      width_px: item.width_px,
      height_px: item.height_px,
      approved_external: item.approved_external,
      quality_score: item.quality_score,
      mime_type: item.mime_type,
      is_vector: item.is_vector,
      has_alpha: item.has_alpha,
      svg_background_removed: item.svg_background_removed,
      thumb_url: thumb,
      web_url: web || thumb,
      original_url: item.original_url,
      transparent_url: rend.transparent?.url,
      logo_display_url: rend.transparent?.url,
      focal_point: typeof item.focal_point === 'object' ? item.focal_point : null,
    };
  }

  async getCompositionAssetCandidates(params = {}) {
    const {
      role = 'photo',
      preset,
      q,
      brief,
      limit = 24,
      require_approved: requireApproved,
    } = params;
    const body = {
      role,
      preset,
      limit,
      q: q || undefined,
    };
    if (requireApproved !== undefined && requireApproved !== null) {
      body.require_approved = requireApproved;
    }
    if (!body.q && brief) {
      body.brief = String(brief).slice(0, 300);
    }
    const fallbackFromListMedia = async (searchQ) => {
      const trimmed = (searchQ || '').trim();
      const searchMode = role === 'logo' ? 'text' : 'semantic';
      let items = [];
      if (trimmed) {
        ({ items } = await this.listMedia({ q: trimmed, limit: limit * 2, search: searchMode }));
        if (!items?.length && role !== 'logo') {
          ({ items } = await this.listMedia({ q: trimmed, limit: limit * 2, search: 'text' }));
        }
      } else {
        ({ items } = await this.listMedia({ limit: limit * 2 }));
      }
      let mapped = (items || []).map((item) => this.mapListMediaToCandidate(item));
      if (role === 'logo') {
        mapped = mapped.filter((row) => {
          if (row.is_vector) return true;
          const name = (row.file_name || '').toLowerCase();
          const folder = (row.folder_path || '').toLowerCase();
          return /logo|scmi|selkirk/.test(name) || /logo/.test(folder);
        });
      }
      return mapped.slice(0, limit);
    };

    try {
      const result = await this.request('compositions/asset-candidates', { method: 'POST', body });
      const rows = result.data || [];
      if (rows.length) {
        return rows;
      }
      const fallbackQ = (q || '').trim() || (brief ? String(brief).slice(0, 80).trim() : '');
      return fallbackFromListMedia(fallbackQ);
    } catch (primaryErr) {
      const fallbackQ = (q || '').trim() || (brief ? String(brief).slice(0, 80).trim() : '');
      if (!fallbackQ) {
        try {
          return fallbackFromListMedia('');
        } catch {
          throw primaryErr;
        }
      }
      return fallbackFromListMedia(fallbackQ);
    }
  }

  /**
   * WYSIWYG PNG via server Chromium (Playwright). Body matches buildStandaloneHtmlDocument output.
   * @returns {Promise<Blob>}
   */
  async exportCompositionRender(compositionId, { html, width, height, scale = 2 }) {
    const headers = await this.getAuthenticatedHeaders();
    const url = `${this.apiUrl}/compositions/${compositionId}/export`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        html,
        width,
        height,
        scale,
        format: 'png',
      }),
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try {
        const parsed = JSON.parse(text);
        message = parsed.message || parsed.error || text;
      } catch {
        // non-JSON body
      }
      throw new Error(message || `Export failed: ${response.status}`);
    }
    return response.blob();
  }

  /** Same-origin blob stream for canvas export (avoids tainted canvas from Azure SAS URLs). */
  async fetchCompositionAssetBlob(assetId, rendition = 'web') {
    const headers = await this.getAuthenticatedHeaders();
    delete headers['Content-Type'];
    const query = new URLSearchParams({ rendition });
    const url = `${this.apiUrl}/compositions/asset/${assetId}?${query}`;
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try {
        const parsed = JSON.parse(text);
        message = parsed.message || parsed.error || text;
      } catch {
        // non-JSON body
      }
      throw new Error(message || `Asset fetch failed: ${response.status}`);
    }
    return response.blob();
  }
}

export const documentDesignService = new DocumentDesignService();
export default documentDesignService;
